import { Color, Spacing } from '@persona-id/design-tokens';
import { set } from 'lodash/fp';
import { FC, Fragment, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import AndRulesSection from 'dashboard/components/WorkflowControlPanel/StepConfigConditional/AndRulesSection';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { BinaryOperator, ConditionalRule } from 'dashboard/lib/workflows';
import { WorkflowSchema } from 'dashboard/models/workflow';
import { WorkflowConditional } from 'dashboard/models/workflow-version';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { Label } from 'design-system/components/Text';
import { JsonSchema } from 'lib/json-schema';

const Well = styled.div`
  background-color: ${Color.CoreSlate50};
  border-radius: ${BORDER_RADIUSES.rounded};
  padding: ${Spacing[2]} ${Spacing[4]};
`;

const Line = styled('hr')`
  overflow: visible; /* For IE */
  padding: 0;
  border: none;
  border-top: solid ${Color.CoreSlate200};
  text-align: center;
  border-top-width: thin;
`;

const StyledButton = styled(Button)`
  background-color: ${Color.CoreSlate200};
  width: ${Spacing[7]};
  height: ${Spacing[5]};
`;

const StyledOrButton = styled(Button)`
  width: 96px;
  height: ${Spacing[6]};
`;

const RouteNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
  justify-content: space-between;
`;

interface EditConditionalPanelProps {
  blockedRightId?: string;
  conditional: WorkflowConditional;
  fullscreenTop?: number;
  jsonSchema: JsonSchema;
  panelTitle: string;
  tokensToDisplayValue: Record<string, string>;
  workflowSchema: WorkflowSchema;
  allowEditName: boolean;
  portalId?: string;
  onClose: () => void;
  updateConditional: (conditional: WorkflowConditional) => void;
}

const EditConditionalPanel: FC<EditConditionalPanelProps> = ({
  blockedRightId,
  conditional,
  fullscreenTop,
  jsonSchema,
  panelTitle,
  tokensToDisplayValue,
  workflowSchema,
  portalId,
  allowEditName = false,
  onClose,
  updateConditional,
}) => {
  const { name, rules } = conditional;
  const [nameStateValue, setNameStateValue] = useState<string>(name);

  const handleChange = useCallback(() => {
    allowEditName &&
      nameStateValue !== name &&
      updateConditional({
        ...conditional,
        name: nameStateValue,
      });
  }, [allowEditName, conditional, name, nameStateValue, updateConditional]);

  const addOrRule = useCallback(() => {
    updateConditional({
      ...conditional,
      rules: {
        or: conditional.rules.or.concat({ and: [{}] }),
      },
    });
  }, [conditional, updateConditional]);

  const addAndRule = useCallback(
    (orIndex: number) => {
      updateConditional({
        ...conditional,
        rules: {
          or: set(
            [orIndex, 'and'],
            conditional.rules.or[orIndex].and.concat({}),
            conditional.rules.or,
          ),
        },
      });
    },
    [conditional, updateConditional],
  );

  const deleteRule = useCallback(
    (orIndex: number, andIndex: number) => {
      const newOrRule = [
        ...conditional.rules.or[orIndex].and.slice(0, andIndex),
        ...conditional.rules.or[orIndex].and.slice(andIndex + 1),
      ];

      const newConditional =
        newOrRule.length === 0
          ? [...conditional.rules.or.slice(0, orIndex), ...conditional.rules.or.slice(orIndex + 1)]
          : [
              ...conditional.rules.or.slice(0, orIndex),
              { and: newOrRule },
              ...conditional.rules.or.slice(orIndex + 1),
            ];
      updateConditional({
        ...conditional,
        rules: {
          or: newConditional,
        },
      });
    },
    [conditional, updateConditional],
  );

  const updateRule = useCallback(
    (orIndex: number, andIndex: number, newRule: ConditionalRule) => {
      updateConditional({
        ...conditional,
        rules: {
          or: set([orIndex, 'and', andIndex], newRule, conditional.rules.or),
        },
      });
    },
    [conditional, updateConditional],
  );

  const orRules = rules[BinaryOperator.Or];

  // Generate a prefix for each orRule's index num to avoid duplication of 'key'
  // When using index in orRules array, deletion of an element can't trigger re-rendering of contents correctly
  // Initial: [A, B, C] -> Delete: Inital[1] -> Renders [A, B] instead of [A, C] since C gets "1" for key
  // However, this approach doesn't work if we need to swap/sort orRules
  // See also: https://github.com/persona-id/persona-web/issues/9475#issuecomment-2156076441
  const [[orRuleIndexPrefix, lastLength], setPrefixAndLength] = useState<[number, number]>([0, 0]);
  useEffect(() => {
    if (orRules.length !== lastLength) {
      setPrefixAndLength([orRuleIndexPrefix + 1, orRules.length]);
    }
  }, [orRules]);

  const content = (
    <MaximizablePanel
      title={panelTitle}
      fullscreenTop={fullscreenTop}
      offsetElementId={blockedRightId}
      outsideExceptionalIdPrefixes={[
        'base-conditional-input-possibilities',
        'base-conditional-row',
      ]}
      onOutsideClickAndEscPress={onClose}
    >
      {allowEditName && (
        <>
          <RouteNameRow>
            <Label htmlFor='edit-conditional-panel__edit-name' size='md' weight='bold'>
              Label
            </Label>
            <Input
              autoFocus
              id='edit-conditional-panel__edit-name'
              value={nameStateValue}
              onBlur={handleChange}
              onChange={(e) => setNameStateValue(e.target.value)}
              aria-label='Conditional name'
            />
          </RouteNameRow>
          <Spacer height={2} />
          <Line />
          <Spacer height={2} />
        </>
      )}
      {orRules.map((andRulesConfig, orIndex) => (
        <Fragment key={`${orRuleIndexPrefix}-${orIndex}`}>
          <Well>
            <Spacer height={1} />
            <AndRulesSection
              andRules={andRulesConfig[BinaryOperator.And]}
              workflowSchema={workflowSchema}
              jsonSchema={jsonSchema}
              tokensToDisplayValue={tokensToDisplayValue}
              updateConditionalRule={(andIndex) => (newRule) =>
                updateRule(orIndex, andIndex, newRule)
              }
              deleteConditional={(andIndex) => () => deleteRule(orIndex, andIndex)}
              orIndex={orIndex}
            />
            <Spacer height={1} />
            <StyledButton
              onClick={() => addAndRule(orIndex)}
              id={`edit-conditional-panel__add-and-rule__${orIndex}`}
              type='button'
              variant='basic'
              width='icon'
              aria-label={`Add a new And rule for group ${orIndex}`}
            >
              And
            </StyledButton>
            <Spacer height={1} />
          </Well>
          <Spacer height={2} />
        </Fragment>
      ))}
      <Spacer height={2} />
      <StyledOrButton
        aria-label='Add a new Or group'
        type='button'
        variant='basic'
        size='sm'
        width='button'
        onClick={addOrRule}
        id='edit-conditional-panel__add-or-rule'
      >
        Or group
      </StyledOrButton>
    </MaximizablePanel>
  );

  const portalParent: Element | null = portalId ? document.getElementById(portalId) : null;
  return portalParent ? createPortal(content, portalParent) : content;
};

export default EditConditionalPanel;
